import { isBefore, startOfDay } from 'date-fns';
import { matchPath } from 'react-router';

import { all, call, put, select, take, takeLatest, debounce, takeEvery } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import { SELECT_DEALER } from 'constants/ActionTypes';
import { ANY_SENDERS, CHECK_IN_SENDERS, CHECK_OUT_SENDERS, SELECTED_DATE, SENDERS } from 'constants/FilterConstants';
import { PATH } from 'constants/RoutesConstants';
import { APPLICATION_IS_READY } from 'modules/app/actionTypes';
import { applicationIsReady } from 'modules/app/selectors';
import { simpleRequestsLoader } from 'modules/app/utils';
import * as filterActions from 'modules/interventions/actions/filters';
import * as kanbanActions from 'modules/interventions/actions/kanban';
import * as types from 'modules/interventions/actionTypes';

import { getBaseRequestParams, getFilters, getKanbanSubscription } from 'modules/interventions/selectors/kanban';
import { fetchKiosks } from 'modules/kiosks/actions';
import { getBaseRequestParams as getKiosksBaseRequestParams } from 'modules/kiosks/selectors/selects';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { getLocation } from 'modules/router/selectors';
import { SEND_SELF_SERVICE_SUCCESS } from 'modules/selfServices/actionTypes';
import { subscribeOnPage } from 'modules/sockets/utils.middleware';
import { createNotification } from 'modules/toast-notifications/actions';
import { getUserGroups } from 'modules/userGroups/actions';

import { hasCurrentUserKioskPinLoginEnabled } from 'modules/users/selectors/currentUser';
import { getSelectedDealerId, getTimeZoneOffsetByDealerId } from 'selectors/dealerSelectors';

import { convertTimeZones } from 'utils/date';

const SENDER_FIELDS = [SENDERS, CHECK_IN_SENDERS, CHECK_OUT_SENDERS, ANY_SENDERS];

function* resetKanbanActiveSendersWorker() {
  const filters = yield select(getFilters);
  const activeSenderFields = SENDER_FIELDS.filter(field => filters[field]?.length > 0);
  if (activeSenderFields.length > 0) {
    yield all(activeSenderFields.map(field => put(filterActions.setKanbanFilter(field, []))));
  }
}

function* refreshKanban(action) {
  const location = yield select(getLocation);
  const match = matchPath(PATH.SELF_SERVICE_KANBAN.path, location.pathname);
  if (match) {
    const params = yield select(getBaseRequestParams);
    yield put(kanbanActions.fetchKanbanInterventions(params, action?.meta));
  }
}

function* initKanbanBoard() {
  const location = yield select(getLocation);
  const match = matchPath(PATH.SELF_SERVICE_KANBAN.path, location.pathname);

  if (match) {
    const isReady = yield select(applicationIsReady);
    if (!isReady) {
      yield take(APPLICATION_IS_READY);
    }

    const selectedDealerId = yield select(getSelectedDealerId);
    if (selectedDealerId) {
      yield put(getUserGroups(selectedDealerId));

      // Fetch the kiosks to know if the user has access to the kiosk master pin
      const hasKioskAdminAccess = yield select(hasCurrentUserKioskPinLoginEnabled);
      if (hasKioskAdminAccess) {
        const parameters = yield select(getKiosksBaseRequestParams);
        yield put(fetchKiosks({
          ...parameters,
          page: 0,
        }, { source: 'kanban-dashboard' }));
      }

      // Selected date initialization
      // `selectedDate` is supposed to be UTC
      const { date: selectedDate } = yield select(getKanbanSubscription);
      const dealerTimezone = yield select(state => getTimeZoneOffsetByDealerId(state, selectedDealerId));
      const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // From our timezone to dealer's (12/09 15:13 UTC+2 to 13/09 01:58 UTC+12:45)
      const nowInDealerTZ = convertTimeZones(new Date(), systemTimeZone, dealerTimezone);
      // Start of dealers day (13/09 01:58 UTC+12:45 to 13/09 00:00 UTC+12:45)
      const startOfDealerDay = startOfDay(nowInDealerTZ);
      // Start of dealers day in UTC (13/09 00:00 UTC+12:45 to 12/09 13:15 UTC+2, then 12/09 11:15 UTC as ISO string)
      const startOfDealerDayAsUTC = convertTimeZones(startOfDealerDay, dealerTimezone, systemTimeZone);

      let newDate;
      if (!selectedDate || isBefore(selectedDate, startOfDealerDayAsUTC)) {
        newDate = startOfDealerDayAsUTC.toISOString();
      }

      if (newDate) {
        yield put(filterActions.setKanbanFilter(SELECTED_DATE, newDate));
      } else {
        yield call(refreshKanban);
      }
    }
  }
}

/** @param {import("../types/actions").MoveKanbanInterventionAction} action */
function* moveInterventionKanban({ payload, meta }) {
  const { request, ...rollback } = payload;
  try {
    const {
      data: { [rollback.to]: intervention },
    } = yield call(restClient.request, request);

    yield put(kanbanActions.moveKanbanInterventionSuccess(intervention, rollback.to, meta));
  } catch (axiosError) {
    yield put(kanbanActions.moveKanbanInterventionFailure(axiosError, rollback, meta));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'intervention.error.moveFailed',
          defaultMessage: 'This card could not be moved.',
        },
      })
    );
    yield call(refreshKanban);
  }
}

function* afterKanbanWebSocketUpdate({ payload }) {
  const params = yield select(getBaseRequestParams);
  yield put(
    kanbanActions.fetchKanbanInterventions(
      {
        ...params,
        ids: [payload.entityId],
      },
      {
        ...payload.data?.flags,
        silent: true,
      }
    )
  );
}

const kanbanSocketModifiers = [
  SELECT_DEALER,
  types.CLEAR_KANBAN_INTERVENTION_FILTER,
  ({ type, payload }) => type === types.SET_KANBAN_INTERVENTION_FILTER && payload.field === SELECTED_DATE,
];

const kanbanSocketUpdate = ({ type }) => {
  const action = type.split('/').pop();
  return type.startsWith('@@SOCKET/TOPIC:KANBAN/INTERVENTION') && !['CLOSED', 'DELETE'].includes(action);
};

/** @type {Record<string, import("modules/app/types").SimpleRequestConfig>} */
const simpleRequests = {
  [types.FETCH_KANBAN_INTERVENTIONS]: {
    callbacks: {
      success: (data, meta, { payload: { request } }) =>
        kanbanActions.fetchKanbanInterventionsSuccess(data, { ...meta, ids: request.params.ids }),
    },
  },
};

export default function* interventionsKanbanSagas() {
  yield all(simpleRequestsLoader(simpleRequests));

  yield takeLatest([LOCATION_CHANGE, SELECT_DEALER], initKanbanBoard);
  yield takeLatest(
    [
      types.SET_KANBAN_INTERVENTION_FILTER,
      types.CLEAR_KANBAN_INTERVENTION_FILTER,
      types.REMOVE_KANBAN_INTERVENTION_FILTER,
      SEND_SELF_SERVICE_SUCCESS
    ],
    refreshKanban,
  );
  yield takeLatest(types.SET_KANBAN_SENDER_FILTER_MODE, resetKanbanActiveSendersWorker);
  yield debounce(150, [types.MOVE_KANBAN_INTERVENTION], moveInterventionKanban);

  yield takeEvery(kanbanSocketUpdate, afterKanbanWebSocketUpdate);
  yield subscribeOnPage([PATH.SELF_SERVICE_KANBAN], 'KANBAN', getKanbanSubscription, kanbanSocketModifiers);
}
